import {
  Alert,
  Badge,
  Button,
  Divider,
  Drawer,
  Grid,
  Group,
  Menu,
  Modal,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import {
  IconArrowBackUp,
  IconCalendarQuestion,
  IconCheck,
  IconDots,
  IconEdit,
  IconTrash,
  IconUpload,
  IconUserShare,
} from '@tabler/icons-react';
import { useEffect, useState, type ReactNode } from 'react';
import type { FileRefDto, RequestDetailDto, WorkItemDto } from '@mexp/shared';
import { http } from '@/api/client';
import { FileList } from '@/features/files/FileList';
import { FileUploader } from '@/features/files/FileUploader';
import { departmentName, useDepartments, useStaffLookup } from '@/features/shared/lookups';
import { formatBusinessDate, fromNow, toBusinessDate } from '@/lib/format';
import { useDrAction } from '../mutations';
import { DueDate, TaskStatusBadge } from './badges';
import { ReasonModal } from './ReasonModal';
import { TaskHistory } from './RequestHistory';
import { TaskVersions } from './TaskVersions';

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Stack gap={6}>
      <Text fz="xs" fw={700} c="dimmed" tt="uppercase" lts={0.4}>
        {title}
      </Text>
      {children}
    </Stack>
  );
}

function ReassignModal({ item, opened, onClose }: { item: WorkItemDto; opened: boolean; onClose: () => void }) {
  const departments = useDepartments();
  const [department, setDepartment] = useState<string | null>(item.department);
  const [assigneeId, setAssigneeId] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const people = useStaffLookup({ department }, opened && !!department);
  useEffect(() => {
    if (opened) {
      setDepartment(item.department);
      setAssigneeId(null);
      setNote('');
    }
  }, [opened, item.department]);
  const reassign = useDrAction(
    () => http.put(`/work-items/${item.id}/assignee`, { assigneeId, department: department ?? undefined, note: note.trim() || undefined }),
    'Task reassigned',
    onClose,
  );
  return (
    <Modal opened={opened} onClose={onClose} title={`Reassign ${item.code}`}>
      <Stack>
        <Text fz="sm" c="dimmed">
          Currently with {item.assignee.name}. Both people are notified.
        </Text>
        <Select
          label="Department"
          data={(departments.data ?? []).map((d) => ({ value: d.code, label: d.name }))}
          value={department}
          onChange={(v) => {
            setDepartment(v);
            setAssigneeId(null);
          }}
          searchable
        />
        <Select
          label="New person"
          data={(people.data ?? []).filter((u) => u.id !== item.assignee.id).map((u) => ({ value: u.id, label: `${u.fullName} · ${u.email}` }))}
          value={assigneeId}
          onChange={setAssigneeId}
          searchable
          nothingFoundMessage="Nobody else in this department"
        />
        <Textarea label="Note (optional)" autosize minRows={2} value={note} onChange={(e) => setNote(e.currentTarget.value)} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!assigneeId} loading={reassign.isPending} onClick={() => reassign.mutate()}>
            Reassign
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function SubmitOnBehalfModal({ item, opened, onClose }: { item: WorkItemDto; opened: boolean; onClose: () => void }) {
  const [files, setFiles] = useState<FileRefDto[]>([]);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (opened) {
      setFiles([]);
      setNote('');
    }
  }, [opened]);
  const submit = useDrAction(
    () => http.post(`/work-items/${item.id}/submissions`, { fileIds: files.map((f) => f.id), note: note.trim() || undefined }),
    'Documents submitted',
    onClose,
  );
  return (
    <Modal opened={opened} onClose={onClose} title={`Upload for ${item.assignee.name}`} size="lg">
      <Stack>
        <Text fz="sm" c="dimmed">
          Use this when the documents reached you another way (for example a department without a login). It is recorded as uploaded on their behalf.
        </Text>
        <FileUploader area="wi_submission" value={files} onChange={setFiles} onBusyChange={setBusy} />
        <Textarea label="Note (optional)" autosize minRows={2} value={note} onChange={(e) => setNote(e.currentTarget.value)} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!files.length || busy} loading={submit.isPending} onClick={() => submit.mutate()}>
            Submit
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function EditTaskModal({ item, opened, onClose }: { item: WorkItemDto; opened: boolean; onClose: () => void }) {
  const [title, setTitle] = useState(item.title);
  const [instructions, setInstructions] = useState(item.instructions ?? '');
  useEffect(() => {
    if (opened) {
      setTitle(item.title);
      setInstructions(item.instructions ?? '');
    }
  }, [opened, item]);
  const save = useDrAction(() => http.patch(`/work-items/${item.id}`, { title: title.trim(), instructions: instructions.trim() || null }), 'Task updated', onClose);
  return (
    <Modal opened={opened} onClose={onClose} title={`Edit ${item.code}`}>
      <Stack>
        <TextInput label="Document to provide" value={title} onChange={(e) => setTitle(e.currentTarget.value)} />
        <Textarea label="Instructions" autosize minRows={2} value={instructions} onChange={(e) => setInstructions(e.currentTarget.value)} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!title.trim()} loading={save.isPending} onClick={() => save.mutate()}>
            Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** A task as seen from the request page. Coordinators act on it here; others read it. */
export function TaskDrawer({
  item,
  request,
  canManage,
  onClose,
}: {
  item: WorkItemDto | null;
  request: RequestDetailDto;
  canManage: boolean;
  onClose: () => void;
}) {
  const departments = useDepartments();
  const [modal, setModal] = useState<null | 'return' | 'reopen' | 'remove' | 'reassign' | 'behalf' | 'edit' | 'verify' | 'reject'>(null);
  const [verifyNote, setVerifyNote] = useState('');
  const close = () => setModal(null);

  const verify = useDrAction(() => http.post(`/work-items/${item!.id}/verify`, { note: verifyNote.trim() || undefined }), 'Marked as verified', () => {
    setVerifyNote('');
    close();
  });
  const returnItem = useDrAction((r: { reason: string; fileIds: string[] }) => http.post(`/work-items/${item!.id}/return`, r), 'Sent back for changes', close);
  const remove = useDrAction((r: { reason: string }) => http.post(`/work-items/${item!.id}/cancel`, r), 'Task removed', () => {
    close();
    onClose();
  });
  const accept = useDrAction(() => http.post(`/work-items/${item!.id}/target-proposal/accept`), 'Target date accepted');
  const reject = useDrAction((r: { reason: string }) => http.post(`/work-items/${item!.id}/target-proposal/reject`, { reason: r.reason || undefined }), 'Proposal declined', close);
  const setDate = useDrAction((patch: { targetDate?: string | null; targetRaBdDate?: string | null }) => http.patch(`/work-items/${item!.id}`, patch), 'Date updated');

  if (!item) return <Drawer opened={false} onClose={onClose} />;
  const open = item.status !== 'verified' && item.status !== 'cancelled';
  const query = item.queryId ? (request.changeRequests.find((c) => c.id === item.queryId) ?? null) : null;
  // After a client-portal release only a task answering a still-open client query can be reopened.
  const reopenable = request.origin !== 'client' || !request.releases.length || !!query?.open;

  return (
    <Drawer opened={!!item} onClose={onClose} size="xl" title={<Text fw={700} ff="monospace">{item.code}</Text>}>
      <Stack gap="lg">
        <Stack gap={6}>
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Title order={4}>{item.title}</Title>
            {canManage && item.status !== 'cancelled' && (
              <Menu position="bottom-end" withinPortal>
                <Menu.Target>
                  <Button variant="default" size="xs" rightSection={<IconDots size={14} />}>
                    More
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item leftSection={<IconEdit size={16} />} onClick={() => setModal('edit')}>
                    Edit description
                  </Menu.Item>
                  {open && (
                    <Menu.Item leftSection={<IconUserShare size={16} />} onClick={() => setModal('reassign')}>
                      Reassign
                    </Menu.Item>
                  )}
                  {open && (
                    <Menu.Item leftSection={<IconUpload size={16} />} onClick={() => setModal('behalf')}>
                      Upload on their behalf
                    </Menu.Item>
                  )}
                  <Menu.Divider />
                  <Menu.Item color="red" leftSection={<IconTrash size={16} />} onClick={() => setModal('remove')}>
                    Remove task
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            )}
          </Group>
          <Group gap="xs">
            <TaskStatusBadge status={item.status} />
            {item.returnCount > 0 && <Badge variant="outline" color="orange">Returned {item.returnCount}×</Badge>}
            <Badge variant="outline" color="gray">
              {departmentName(departments.data, item.department)}
            </Badge>
            {query && (
              <Badge variant="light" color="grape">
                Query {query.number}
              </Badge>
            )}
          </Group>
          <Text fz="sm">
            Assigned to <b>{item.assignee.name}</b>
            {item.assignee.email ? ` (${item.assignee.email})` : ''}
          </Text>
        </Stack>

        {item.targetProposal && (
          <Alert color="yellow" variant="light" icon={<IconCalendarQuestion size={18} />} title={`${item.targetProposal.proposedBy.name} proposed ${formatBusinessDate(item.targetProposal.date)}`}>
            <Stack gap="xs">
              {item.targetProposal.note && <Text fz="sm">“{item.targetProposal.note}”</Text>}
              {canManage && (
                <Group gap="xs">
                  <Button size="xs" color="teal" loading={accept.isPending} onClick={() => accept.mutate()}>
                    Accept date
                  </Button>
                  <Button size="xs" variant="default" onClick={() => setModal('reject')}>
                    Decline
                  </Button>
                </Group>
              )}
            </Stack>
          </Alert>
        )}

        <Grid gutter="md">
          <Grid.Col span={6}>
            <Section title="Target date">
              {canManage ? (
                <DateInput
                  minDate={new Date()}
                  value={item.targetDate}
                  onChange={(v) => setDate.mutate({ targetDate: toBusinessDate(v) })}
                  valueFormat="DD MMM YYYY"
                  clearable
                  placeholder="Not set"
                  size="sm"
                />
              ) : (
                <DueDate date={item.targetDate} open={open} />
              )}
            </Section>
          </Grid.Col>
          <Grid.Col span={6}>
            <Section title="RA → BD date">
              {canManage ? (
                <DateInput
                  minDate={new Date()}
                  value={item.targetRaBdDate}
                  onChange={(v) => setDate.mutate({ targetRaBdDate: toBusinessDate(v) })}
                  valueFormat="DD MMM YYYY"
                  clearable
                  placeholder="Not set"
                  size="sm"
                />
              ) : (
                <Text fz="sm">{formatBusinessDate(item.targetRaBdDate)}</Text>
              )}
            </Section>
          </Grid.Col>
        </Grid>

        {item.instructions && (
          <Section title="Instructions">
            <Text fz="sm" style={{ whiteSpace: 'pre-wrap' }}>
              {item.instructions}
            </Text>
          </Section>
        )}
        {item.attachments.length > 0 && (
          <Section title="Reference files for this task">
            <FileList files={item.attachments} />
          </Section>
        )}

        <Divider />

        {item.lastReturn && item.status === 'returned' && (
          <Alert color="orange" variant="light" icon={<IconArrowBackUp size={18} />} title={`Returned by ${item.lastReturn.returnedBy.name} ${fromNow(item.lastReturn.returnedAt)}`}>
            <Stack gap="xs">
              <Text fz="sm" style={{ whiteSpace: 'pre-wrap' }}>
                {item.lastReturn.reason}
              </Text>
              {item.lastReturn.files.length > 0 && <FileList files={item.lastReturn.files} />}
            </Stack>
          </Alert>
        )}

        <Section title={item.queryId ? 'Compliance' : 'Versions'}>
          <TaskVersions item={item} />
        </Section>

        {canManage && item.status === 'submitted' && (
          <Group>
            <Button color="teal" leftSection={<IconCheck size={16} />} onClick={() => setModal('verify')}>
              Verify
            </Button>
            <Button color="orange" variant="light" leftSection={<IconArrowBackUp size={16} />} onClick={() => setModal('return')}>
              Return for changes
            </Button>
          </Group>
        )}
        {item.status === 'verified' && (
          <Alert color="teal" variant="light" icon={<IconCheck size={18} />} title={`Verified by ${item.verifiedBy?.name ?? '—'} ${fromNow(item.verifiedAt)}`}>
            <Stack gap="xs">
              {item.verificationNote && <Text fz="sm">{item.verificationNote}</Text>}
              {canManage && reopenable && (
                <Button size="xs" variant="default" w="fit-content" onClick={() => setModal('reopen')}>
                  Reopen for changes
                </Button>
              )}
            </Stack>
          </Alert>
        )}
        {item.status === 'cancelled' && (
          <Alert color="gray" variant="light" title="This task was removed">
            {item.cancelReason}
          </Alert>
        )}

        <Divider />
        <Section title="History">
          <TaskHistory taskId={item.id} />
        </Section>
      </Stack>

      <Modal opened={modal === 'verify'} onClose={close} title={`Verify ${item.code}`}>
        <Stack>
          <Text fz="sm">Confirm the submitted documents meet the requirement for {request.productName}.</Text>
          <Textarea label="Note (optional)" autosize minRows={2} value={verifyNote} onChange={(e) => setVerifyNote(e.currentTarget.value)} />
          <Group justify="flex-end">
            <Button variant="default" onClick={close}>
              Cancel
            </Button>
            <Button color="teal" loading={verify.isPending} onClick={() => verify.mutate()}>
              Verify
            </Button>
          </Group>
        </Stack>
      </Modal>
      <ReasonModal
        opened={modal === 'return' || modal === 'reopen'}
        onClose={close}
        title={modal === 'reopen' ? `Reopen ${item.code}` : `Return ${item.code}`}
        description={`${item.assignee.name} will be asked to upload corrected documents.`}
        label="Query details?"
        confirmLabel="Send back"
        fileArea="wi_return"
        loading={returnItem.isPending}
        onSubmit={(r) => returnItem.mutate(r)}
      />
      <ReasonModal
        opened={modal === 'remove'}
        onClose={close}
        title={`Remove ${item.code}`}
        description="The person is told the task is no longer needed. Files already submitted stay in the history."
        confirmLabel="Remove task"
        confirmColor="red"
        loading={remove.isPending}
        onSubmit={(r) => remove.mutate({ reason: r.reason })}
      />
      <ReasonModal
        opened={modal === 'reject'}
        onClose={close}
        title="Decline the proposed date"
        label="Reason (optional)"
        optional
        confirmLabel="Decline"
        loading={reject.isPending}
        onSubmit={(r) => reject.mutate({ reason: r.reason })}
      />
      <ReassignModal item={item} opened={modal === 'reassign'} onClose={close} />
      <SubmitOnBehalfModal item={item} opened={modal === 'behalf'} onClose={close} />
      <EditTaskModal item={item} opened={modal === 'edit'} onClose={close} />
    </Drawer>
  );
}
